import { computed, onMounted, onUnmounted, ref, watch, watchEffect, useHost, type Ref, type ComputedRef } from "vue";
import DOMPurify from "dompurify";
import { IMAGE_PRIORITIES, type ImageProps, type ImageType, type ImagePriority } from "./image.types";
import { imageMediaClasses } from "./image.styles";
import { mergeClassProp } from "@/runtime/style-engine/define-variants";
import { parseBoolAttr } from "@/runtime/shared/attrs";
import { useLazyReveal } from "composables/useLazyReveal";
import { useResponsiveImage } from "composables/useResponsiveImage";

const SVG_CACHE_MAX = 50;
const svgCache = new Map<string, Promise<string>>();

const touchSvgCache = (key: string): Promise<string> | undefined => {
  const value = svgCache.get(key);
  if (value === undefined) return undefined;
  svgCache.delete(key);
  svgCache.set(key, value);
  return value;
};

const insertSvgCache = (key: string, value: Promise<string>): void => {
  if (svgCache.has(key)) svgCache.delete(key);
  svgCache.set(key, value);
  while (svgCache.size > SVG_CACHE_MAX) {
    const oldest = svgCache.keys().next().value;
    if (oldest === undefined) break;
    svgCache.delete(oldest);
  }
};

/** @internal Test-only escape hatch for cleaning the SVG cache. */
export const __resetImageSvgCache = (): void => {
  svgCache.clear();
};

/**
 * Ref-counted registry of active `<link rel="preload" as="image">` tags keyed
 * by absolute href. Multiple `<e-image preload>` instances pointing at the
 * same src share one DOM node; the last one to unmount removes it.
 */
const preloadRegistry = new Map<string, { link: HTMLLinkElement; count: number }>();

const acquirePreload = (href: string, priority: ImagePriority): void => {
  if (typeof document === "undefined") return;
  try {
    new URL(href, typeof location === "undefined" ? undefined : location.href);
  } catch {
    return;
  }
  const existing = preloadRegistry.get(href);
  if (existing) {
    existing.count += 1;
    return;
  }
  const link = document.createElement("link");
  link.setAttribute("rel", "preload");
  link.setAttribute("as", "image");
  link.setAttribute("href", href);
  if (priority !== "auto") link.setAttribute("fetchpriority", priority);
  document.head.append(link);
  preloadRegistry.set(href, { link, count: 1 });
};

const releasePreload = (href: string): void => {
  const entry = preloadRegistry.get(href);
  if (!entry) return;
  entry.count -= 1;
  if (entry.count <= 0) {
    entry.link.remove();
    preloadRegistry.delete(href);
  }
};

/** @internal Test-only escape hatch for cleaning module-level registry. */
export const __resetImagePreloadRegistry = (): void => {
  for (const { link } of preloadRegistry.values()) link.remove();
  preloadRegistry.clear();
};

const detectType = (src: string): ImageType => {
  const lower = src.toLowerCase().split("?")[0];
  if (lower.endsWith(".svg")) return "vector";
  return "image";
};

/**
 * Sanitizes an SVG string via DOMPurify before innerHTML injection.
 * Covers script injection, foreignObject, on* handlers, javascript: hrefs,
 * data: URIs, external use/image references, and CSS url() injection —
 * bypass vectors that a hand-rolled sanitizer cannot reliably block.
 * Returns an empty string when the input fails to parse as SVG.
 */
const sanitizeSvg = (svgText: string): string => {
  if (typeof DOMParser === "undefined") return "";
  const doc = new DOMParser().parseFromString(svgText, "image/svg+xml");
  const root = doc.documentElement;
  if (root.nodeName !== "svg" || root.querySelector("parsererror")) return "";
  return DOMPurify.sanitize(svgText, {
    USE_PROFILES: { svg: true, svgFilters: true },
    FORBID_TAGS: ["script", "foreignObject"],
    FORBID_ATTR: ["xlink:href"],
  });
};

export const __sanitizeSvg = sanitizeSvg;

const fetchSvg = (src: string, priority: ImagePriority): Promise<string> => {
  const hit = touchSvgCache(src);
  if (hit !== undefined) return hit;
  const init: RequestInit & { priority?: ImagePriority } = {};
  if (priority !== "auto") init.priority = priority;
  const promise = fetch(src, init)
    .then((res) => {
      if (!res.ok) throw new Error(`Failed to fetch SVG: ${src}`);
      const ct = res.headers.get("content-type") ?? "";
      if (!ct.includes("svg") && !ct.includes("xml") && !ct.includes("text/plain")) {
        throw new Error(`Not an SVG response (${ct}): ${src}`);
      }
      return res.text();
    })
    .then((text) => {
      const trimmed = text.trimStart();
      if (!trimmed.startsWith("<svg") && !trimmed.startsWith("<?xml")) {
        throw new Error(`Response is not an SVG: ${src}`);
      }
      const sanitized = sanitizeSvg(text);
      if (!sanitized) throw new Error(`SVG failed sanitisation: ${src}`);
      return sanitized;
    })
    .catch((error) => {
      svgCache.delete(src);
      throw error;
    });
  insertSvgCache(src, promise);
  return promise;
};

/** Keeps inline SVG in sync with vectorRef DOM node. Returns a load function. */
const setupSvgSync = (
  vectorRef: Ref<HTMLElement | null>,
  svgContent: Ref<string>,
  onLoad: () => void,
): ((url: string, priority: ImagePriority) => Promise<void>) => {
  // Single reactive subscription: writes innerHTML when both refs are populated.
  // Covers both initial mount (svgContent arrives after vectorRef) and re-mount
  // (vectorRef arrives after svgContent, e.g., when switching from raster to vector).
  watchEffect(() => {
    const el = vectorRef.value;
    const html = svgContent.value;
    if (el && html) el.innerHTML = html;
  });
  return async (url: string, priority: ImagePriority): Promise<void> => {
    if (!url) return;
    try {
      svgContent.value = await fetchSvg(url, priority);
      onLoad();
    } catch {
      svgContent.value = "";
    }
  };
};

export interface UseImageOptions {
  props: Readonly<ImageProps>;
  vectorRef: Ref<HTMLElement | null>;
}

export interface UseImageReturn {
  resolvedType: ComputedRef<ImageType>;
  isLoaded: Ref<boolean>;
  svgContent: Ref<string>;
  mediaClasses: ComputedRef<ReturnType<typeof imageMediaClasses>>;
  wrapperClasses: ComputedRef<string>;
  imgSrc: ComputedRef<string>;
  srcsetAttr: ComputedRef<string | undefined>;
  sizesAttr: ComputedRef<string | undefined>;
  fetchPriorityAttr: ComputedRef<ImagePriority | undefined>;
  loadingAttr: ComputedRef<"lazy" | undefined>;
  showSkeleton: ComputedRef<boolean>;
  skeletonClasses: ComputedRef<string>;
  onImgLoad: () => void;
}

interface ImageComputeds {
  resolvedType: ComputedRef<ImageType>;
  isLazy: ComputedRef<boolean>;
  isAutoSizes: ComputedRef<boolean>;
  isPreload: ComputedRef<boolean>;
  isSkeleton: ComputedRef<boolean>;
  isRounded: ComputedRef<boolean>;
  resolvedPriority: ComputedRef<ImagePriority>;
  mediaClasses: ComputedRef<ReturnType<typeof imageMediaClasses>>;
  wrapperClasses: ComputedRef<string>;
  fetchPriorityAttr: ComputedRef<ImagePriority | undefined>;
}

const buildImageComputeds = (props: Readonly<ImageProps>): ImageComputeds => {
  const resolvedType = computed<ImageType>(() => props.type ?? detectType(props.src));
  const isLazy = computed(() => parseBoolAttr(props.lazy, false));
  const isRounded = computed(() => parseBoolAttr(props.rounded, resolvedType.value === "image"));
  const isAutoSizes = computed(() => parseBoolAttr(props.autoSizes, false));
  const isPreload = computed(() => parseBoolAttr(props.preload, false));
  const isSkeleton = computed(() => parseBoolAttr(props.skeleton, true));
  const resolvedPriority = computed<ImagePriority>(() => {
    const raw = props.priority;
    return raw && (IMAGE_PRIORITIES as readonly string[]).includes(raw) ? raw : "auto";
  });
  const fetchPriorityAttr = computed<ImagePriority | undefined>(() =>
    resolvedType.value === "image" && resolvedPriority.value !== "auto" ? resolvedPriority.value : undefined,
  );
  const mediaClasses = computed(() => imageMediaClasses({ rounded: isRounded.value ? "true" : "false" }));
  const wrapperClasses = computed(() => mergeClassProp("relative block w-full h-full", props.class));
  return {
    resolvedType,
    isLazy,
    isAutoSizes,
    isPreload,
    isSkeleton,
    isRounded,
    resolvedPriority,
    mediaClasses,
    wrapperClasses,
    fetchPriorityAttr,
  };
};

/** Manage preload `<link>` lifecycle for this image instance. Returns a sync function and cleanup. */
const createPreloadController = (
  props: Readonly<ImageProps>,
  isPreload: ComputedRef<boolean>,
  resolvedType: ComputedRef<ImageType>,
  resolvedPriority: ComputedRef<ImagePriority>,
): { sync: () => void; release: () => void } => {
  let activeHref: string | null = null;
  let warned = false;
  const sync = (): void => {
    if (isPreload.value && resolvedType.value !== "image" && !warned) {
      console.warn(`[e-image] preload is only supported for raster images (resolvedType="${resolvedType.value}")`);
      warned = true;
    }
    const shouldPreload = isPreload.value && resolvedType.value === "image" && !!props.src;
    const nextHref = shouldPreload ? props.src : null;
    if (nextHref === activeHref) return;
    if (activeHref) releasePreload(activeHref);
    if (nextHref) acquirePreload(nextHref, resolvedPriority.value);
    activeHref = nextHref;
  };
  const release = (): void => {
    if (activeHref) {
      releasePreload(activeHref);
      activeHref = null;
    }
  };
  return { sync, release };
};

interface SrcReactionDeps {
  props: Readonly<ImageProps>;
  resolvedType: ComputedRef<ImageType>;
  resolvedPriority: ComputedRef<ImagePriority>;
  isLoaded: Ref<boolean>;
  svgContent: Ref<string>;
  isVisible: Ref<boolean>;
  reattach: () => void;
  loadSvg: (url: string, priority: ImagePriority) => Promise<void>;
  syncPreload: () => void;
}

/** Wire src/type/preload/priority changes back into internal state. */
const setupSrcReaction = (deps: SrcReactionDeps): void => {
  const { props, resolvedType, resolvedPriority, isLoaded, svgContent, isVisible, reattach, loadSvg, syncPreload } =
    deps;
  watch(
    () => [props.src, props.type, props.preload, props.priority] as const,
    ([newSrc], [oldSrc, oldType]) => {
      if (newSrc !== oldSrc || props.type !== oldType) {
        isLoaded.value = false;
        svgContent.value = "";
        isVisible.value = false;
        reattach();
        if (resolvedType.value === "vector") void loadSvg(newSrc, resolvedPriority.value);
      }
      syncPreload();
    },
  );
};

/** Build the `imgSrc`, `srcsetAttr`, `sizesAttr` triplet used by the raster path. */
const buildRasterSourceAttrs = (
  props: Readonly<ImageProps>,
  observeTarget: Element | null,
  resolvedType: ComputedRef<ImageType>,
  isLazy: ComputedRef<boolean>,
  isAutoSizes: ComputedRef<boolean>,
  isVisible: Ref<boolean>,
): {
  imgSrc: ComputedRef<string>;
  srcsetAttr: ComputedRef<string | undefined>;
  sizesAttr: ComputedRef<string | undefined>;
} => {
  const imgSrc = computed<string>(() =>
    resolvedType.value !== "image" || (isLazy.value && !isVisible.value) ? "" : props.src,
  );
  const { srcsetAttr, sizesAttr } = useResponsiveImage({
    elementRef: observeTarget,
    srcset: () => (resolvedType.value === "image" ? props.srcset : undefined),
    sizes: () => props.sizes,
    autoSizes: () => resolvedType.value === "image" && isAutoSizes.value,
  });
  return { imgSrc, srcsetAttr, sizesAttr };
};

/** Wire mount/unmount hooks and the source-change reaction. */
const setupImageLifecycle = (
  props: Readonly<ImageProps>,
  resolvedType: ComputedRef<ImageType>,
  resolvedPriority: ComputedRef<ImagePriority>,
  preload: { sync: () => void; release: () => void },
  loadSvg: (url: string, priority: ImagePriority) => Promise<void>,
  reactionState: Pick<SrcReactionDeps, "isLoaded" | "svgContent" | "isVisible" | "reattach">,
): void => {
  onMounted(() => {
    preload.sync();
    if (resolvedType.value === "vector") void loadSvg(props.src, resolvedPriority.value);
  });
  onUnmounted(preload.release);
  setupSrcReaction({
    props,
    resolvedType,
    resolvedPriority,
    loadSvg,
    syncPreload: preload.sync,
    ...reactionState,
  });
};

const SKELETON_BASE = "absolute inset-0 animate-pulse bg-(--color-elevation-200)/50";

export const useImage = ({ props, vectorRef }: UseImageOptions): UseImageReturn => {
  const host = useHost();
  const dispatch = (name: string): void => {
    if (host) host.dispatchEvent(new CustomEvent(name, { bubbles: true, composed: true }));
  };

  const isLoaded = ref(false);
  const svgContent = ref("");

  const computeds = buildImageComputeds(props);
  const { resolvedType, isLazy, isAutoSizes, isPreload, isSkeleton, isRounded, resolvedPriority } = computeds;

  const markLoaded = (): void => {
    isLoaded.value = true;
    dispatch("load");
  };

  const { isVisible, reattach } = useLazyReveal({ elementRef: host, enabled: isLazy });
  const loadSvg = setupSvgSync(vectorRef, svgContent, markLoaded);
  const preload = createPreloadController(props, isPreload, resolvedType, resolvedPriority);
  const { imgSrc, srcsetAttr, sizesAttr } = buildRasterSourceAttrs(
    props,
    host,
    resolvedType,
    isLazy,
    isAutoSizes,
    isVisible,
  );

  const showSkeleton = computed(() => isSkeleton.value && !isLoaded.value);
  const skeletonClasses = computed(() => (isRounded.value ? `${SKELETON_BASE} rounded-lg` : SKELETON_BASE));
  const loadingAttr = computed<"lazy" | undefined>(() => (isLazy.value ? "lazy" : undefined));

  setupImageLifecycle(props, resolvedType, resolvedPriority, preload, loadSvg, {
    isLoaded,
    svgContent,
    isVisible,
    reattach,
  });

  return {
    resolvedType,
    isLoaded,
    svgContent,
    mediaClasses: computeds.mediaClasses,
    wrapperClasses: computeds.wrapperClasses,
    imgSrc,
    srcsetAttr,
    sizesAttr,
    fetchPriorityAttr: computeds.fetchPriorityAttr,
    loadingAttr,
    showSkeleton,
    skeletonClasses,
    onImgLoad: markLoaded,
  };
};
